// @ts-nocheck
import { Video } from '../../types';
import { textSearch, getDetails, geocodeAddress, buildMapsUrl } from './placesClient';
import { cluesFromMeta, makeQueries } from '../search/makeQueries';
import { synthesizePlaceQueries } from '../search/querySynthesis';
import { scoreCandidate } from '../search/rank';

type EnrichOptions = {
  signal?: AbortSignal;
  radius?: number; // meters for bias circle (default 20km)
  minConfidence?: number; // 0..1 (default 0.25)
};

function unique<T>(arr: (T | null | undefined | '' )[]): T[] {
  return Array.from(new Set(arr.filter(Boolean) as T[]));
}

// Helper: pick the first photo from Google Places and its attribution
function pickFirstPhotoWithAttribution(photos: any[] = []): { url: string | null; attribution: string | null } {
  if (!photos.length) return { url: null, attribution: null };
  const first = photos[0];
  if (!first) return { url: null, attribution: null };
  let url: string | null = null;
  
  // Use our secure image proxy instead of exposing API keys or problematic getUrl()
  if (first.photo_reference) {
    // Use our image proxy endpoint that handles Google Photos properly
    url = `/img?photo_ref=${encodeURIComponent(first.photo_reference)}&w=800`;
  } else if (first.getUrl) {
    // Fallback: try to extract photo_reference from getUrl if possible
    try {
      const tempUrl = first.getUrl({ maxWidth: 800, maxHeight: 600 });
      const urlMatch = tempUrl.match(/photoreference=([^&]+)/);
      if (urlMatch) {
        url = `/img?photo_ref=${encodeURIComponent(urlMatch[1])}&w=800`;
      }
    } catch {
      // If getUrl fails, skip this photo
      url = null;
    }
  }
  
  const attribution = first.html_attributions?.[0] || null;
  return { url, attribution };
}

export async function enrichVideoWithPlaces(video: Video, opts: EnrichOptions = {}): Promise<Video> {
  // We proceed even if some fields exist so we can still fetch a Places photo when missing

  const { signal, radius = 20_000, minConfidence = 0.25 } = opts;

  try {
    const cityHint = (video.restaurant_location && video.restaurant_location !== 'N/A')
      ? video.restaurant_location
      : undefined;

    // Build clues from title + city hint
    const meta = { title: video.title || '' };
    const clues = {
      ...cluesFromMeta(meta, cityHint),
      targetName: (video.restaurant_name && video.restaurant_name !== 'N/A') ? video.restaurant_name : undefined,
      cuisine: (video.restaurant_cuisine && video.restaurant_cuisine !== 'N/A') ? video.restaurant_cuisine : undefined,
      // Pass through extractor-provided hints if present
      extraNameHints: Array.isArray((video as any)._signals_name_hints) ? (video as any)._signals_name_hints : undefined,
      extraLocHints: Array.isArray((video as any)._signals_loc_hints) ? (video as any)._signals_loc_hints : undefined,
      visionNames: Array.isArray((video as any)._vision_names) ? (video as any)._vision_names : undefined,
      visionLocs: Array.isArray((video as any)._vision_locs) ? (video as any)._vision_locs : undefined,
    } as any;

  const name = (video.restaurant_name && video.restaurant_name !== 'N/A') ? video.restaurant_name : '';
    const addr = (video.restaurant_address && video.restaurant_address !== 'N/A') ? video.restaurant_address : '';
    const locText = (video.restaurant_location && video.restaurant_location !== 'N/A') ? video.restaurant_location : '';
  const creator = (video.creator_name && video.creator_name !== 'N/A') ? video.creator_name : '';

    // Seed queries with explicit name/address/location if present
    const nameQueries = unique<string>([
      name && locText ? `${name} ${locText}` : '',
      name && addr ? `${name} ${addr}` : '',
      name ? `${name} restaurant ${locText}`.trim() : '',
      // Note: Creator names removed to prevent false matches (e.g., "Diana y Aarón" matching "Diana Restaurant")
    ]);
    // Address-only queries: when the caption contains a street address, use it directly — Maps will reveal the POI
    const addressQueries = unique<string>([
      addr || '',
      addr && locText ? `${addr} ${locText}` : '',
    ]);

    // Clue-driven queries
    const clueQueries = makeQueries(clues);

    // Optional nano synthesis to sharpen queries
    let nanoQueries: string[] = [];
    let nanoCityHint = '';
    try {
      const synth = await synthesizePlaceQueries({
        title: (video as any)?.title || '',
        description: (video as any)?.description || '',
        hashtags: (video as any)?.hashtags || [],
        ocr: [],
        cityHint: clues.cityGuess || '',
        language: 'es',
      });
      if (synth) { nanoQueries = synth.queries || []; nanoCityHint = (synth as any).city_hint || ''; }
    } catch {}

    // Final set (cap at 6 to avoid hammering the quota)
    const queries = unique<string>([...addressQueries, ...nameQueries, ...clueQueries, ...nanoQueries]).slice(0, 8);
    if (!queries.length) return video;

    // Geocode bias if we have a city/location hint
    let biasLocation: { lat: number; lng: number } | undefined;
  if (addr || nanoCityHint || locText) {
      try {
        const g = await geocodeAddress(addr || nanoCityHint || locText);
        if (g) biasLocation = g;
      } catch { /* non-fatal */ }
    }

    // Fire all text searches (small burst, handled by your internal retry)
    const settled = await Promise.allSettled(
      queries.map((q) => textSearch(q, biasLocation ? { location: biasLocation, radius } : undefined, { signal }))
    );

    // Collect & dedupe by place_id
    const byId = new Map<string, google.maps.places.PlaceResult>();
    for (const r of settled) {
      if (r.status !== 'fulfilled' || !r.value) continue;
      for (const p of r.value) {
        const pid = p.place_id;
        if (!pid) continue;
        // keep the first or the one with geometry
        if (!byId.has(pid) || (p.geometry && !byId.get(pid)?.geometry)) {
          byId.set(pid, p);
        }
      }
    }
  if (byId.size === 0) return video;

    // Rank by heuristic score (0..1)
  const candidates = Array.from(byId.values()).map((p) => {
    const r = scoreCandidate(p as any, clues);
    return { p, score: (r as any).score ?? r, reasons: (r as any).reasons || [] };
  });
    candidates.sort((a, b) => b.score - a.score);

  // Heuristic: if runner-up has a very strong name match and top is weak, swap
    if (candidates.length > 1 && candidates[0].score < 0.55) {
      const runner = candidates[1];
      const tgt = (clues as any)?.targetName || '';
      const n = (runner.p as any)?.name?.toString()?.toLowerCase() || '';
      const t = tgt.toString().toLowerCase();
      if (t && (n === t || n.includes(t) || t.includes(n)) && runner.score >= candidates[0].score + 0.1) {
        [candidates[0], candidates[1]] = [candidates[1], candidates[0]];
      }
    }

  const top = candidates[0];
  // Guard: if we only have an address and no name signals, and the top candidate is not restaurant-like, cap its score to force confirm
  const restaurantTypeSet = new Set(['restaurant','food','cafe','bakery','bar','meal_takeaway','meal_delivery']);
  const hasNameSignal = Boolean((clues as any)?.targetName) || (Array.isArray((clues as any)?.extraNameHints) && (clues as any).extraNameHints.length > 0) || (Array.isArray((clues as any)?.visionNames) && (clues as any).visionNames.length > 0);
  if (top) {
    const tps = Array.isArray((top.p as any)?.types) ? (top.p as any).types as string[] : [];
    const isRestaurantLike = tps.some(t => restaurantTypeSet.has(t));
    if (!isRestaurantLike && !hasNameSignal && addr) {
      top.score = Math.min(top.score, 0.59);
    }
  }
  const suggestions = candidates.slice(0, 3).map((c) => ({
    place_id: (c.p as any)?.place_id || null,
    name: (c.p as any)?.name || null,
    address: (c.p as any)?.formatted_address || null,
    enrich_confidence: c.score,
  }));

    // Optional OpenAI tie-breaker for ambiguous cases (DISABLED in production and by default)
    // This previously used a client-side OpenAI key when present. To avoid any chance of
    // exposing keys in the browser, the reranker is disabled. Keep server-side AI only.
    // If needed later, route through /functions/ai securely.
    try {
      /* disabled: client-side reranker */
    } catch {
      // no-op
    }
    if (!top || !top.p.place_id) {
      return video;
    }

  const winner = candidates[0];
  if (winner.score < minConfidence) {
      // Low confidence: attach only safe enrichments (place URL, photo, attribution, reviews),
      // and fill rating if missing — do NOT overwrite name/address/cuisine.
  try {
  const d = await getDetails(winner.p.place_id, { signal });
        if (!d) return video;

        const mapsUrl = (d as any).url || buildMapsUrl(d.place_id, d.name, d.formatted_address);

        // Use the first Google photo if available
        let bestPhoto: string | null = null;
        let photoAttribution: string | null = null;
        if (Array.isArray(d.photos) && d.photos.length) {
          const picked = pickFirstPhotoWithAttribution(d.photos);
          bestPhoto = picked.url;
          photoAttribution = picked.attribution;
        }

        // Reviews (latest up to 10)
        let reviews: any[] = [];
        if (Array.isArray(d.reviews) && d.reviews.length) {
          const sorted = d.reviews
            .filter((r: any) => r.text && r.text.length > 10)
            .sort((a: any, b: any) => {
              // Prefer newer reviews; fall back to rating if no relative time
              const at = (a.time || 0);
              const bt = (b.time || 0);
              if (bt !== at) return bt - at;
              return (b.rating || 0) - (a.rating || 0);
            });
          reviews = sorted
            .slice(0, 10)
            .map((r: any) => ({
              author_name: r.author_name,
              rating: r.rating,
              text: r.text,
              relative_time: r.relative_time_description,
              profile_photo_url: r.profile_photo_url,
            }));
        }

        return {
          ...video,
          evidence_queries_used: queries,
          evidence_city_hint: nanoCityHint || locText || null,
          google_maps_url: mapsUrl || video.google_maps_url || 'N/A',
          places_photo_url: bestPhoto || (video as any).places_photo_url || null,
          photo_attribution_html: photoAttribution ?? (video as any).photo_attribution_html,
          reviews: reviews.length ? reviews : video.reviews,
          restaurant_rating: typeof video.restaurant_rating === 'number' && video.restaurant_rating > 0
            ? video.restaurant_rating
            : (typeof (d as any).rating === 'number' ? (d as any).rating : video.restaurant_rating),
          open_now: (d as any)?.opening_hours?.isOpen?.() ?? null,
          opening_hours_weekday_text: (d as any)?.opening_hours?.weekday_text || null,
          enrich_confidence: winner.score,
          enrich_place_name: (d as any)?.name || (winner.p as any)?.name || null,
          enrich_place_address: (d as any)?.formatted_address || null,
          enrich_place_id: (d as any)?.place_id || (winner.p as any)?.place_id || null,
          enrich_place_lat: typeof (d as any)?.geometry?.location?.lat === 'function' ? (d as any).geometry.location.lat() : (d as any)?.geometry?.location?.lat || null,
          enrich_place_lng: typeof (d as any)?.geometry?.location?.lng === 'function' ? (d as any).geometry.location.lng() : (d as any)?.geometry?.location?.lng || null,
          enrich_suggestions: suggestions,
        } as any;
      } catch {
        return video;
      }
    }

  // Details for the winner; prefer persisted place_id from video when present to avoid mismatches
  const persistedId = (video as any).enrich_place_id as string | undefined;
  const d = await getDetails(persistedId || winner.p.place_id, { signal });
    if (!d) return video;

    const mapsUrl = (d as any).url || buildMapsUrl(d.place_id, d.name, d.formatted_address);

  // Pick a good photo (not a menu) and attribution
    let bestPhoto = null;
    let photoAttribution = null;
    if (Array.isArray(d.photos) && d.photos.length) {
      const picked = pickFirstPhotoWithAttribution(d.photos);
      bestPhoto = picked.url;
      photoAttribution = picked.attribution;
    }

    // Attach reviews (latest up to 10)
    let reviews = [];
    if (Array.isArray(d.reviews) && d.reviews.length) {
      const sorted = d.reviews
        .filter((r: any) => r.text && r.text.length > 10)
        .sort((a: any, b: any) => {
          const at = (a.time || 0);
          const bt = (b.time || 0);
          if (bt !== at) return bt - at;
          return (b.rating || 0) - (a.rating || 0);
        });
      reviews = sorted
        .slice(0, 10)
        .map(r => ({
          author_name: r.author_name,
          rating: r.rating,
          text: r.text,
          relative_time: r.relative_time_description,
          profile_photo_url: r.profile_photo_url,
        }));
    }

  // Thumbnail priority: still keep existing thumbnail_url as-is (it may be provider/OG)
  // but expose a dedicated places_photo_url for UI to prefer.
  const thumbnail = video.thumbnail_url && video.thumbnail_url !== 'N/A' ? video.thumbnail_url : (bestPhoto || 'N/A');

    function mapCuisineFromTypes(types: string[] | undefined): string | null {
      if (!Array.isArray(types) || types.length === 0) return null;
      const t = types.join(' ').toLowerCase();
      const checks: Array<[RegExp, string]> = [
        [/ramen|noodle/, 'Ramen'],
        [/japanese/, 'Japanese'],
        [/mexican|taco|taqueria/, 'Mexican'],
        [/italian|pizza|pizzeria|trattoria/, 'Italian'],
        [/thai/, 'Thai'],
        [/korean|bbq.*korean/, 'Korean'],
        [/chinese|dimsum|dim_sum/, 'Chinese'],
        [/indian|curry|tandoori/, 'Indian'],
        [/mediterranean|greek|lebanese|turkish/, 'Mediterranean'],
        [/seafood|mariscos/, 'Seafood'],
        [/steak|chophouse/, 'Steakhouse'],
        [/burger|hamburg/, 'Burgers'],
        [/barbecue|bbq/, 'Barbecue'],
        [/cafe|coffee_shop|coffee/, 'Cafe'],
        [/bakery|pasteleria|panaderia|patisserie/, 'Bakery'],
        [/dessert|ice_cream|gelato/, 'Dessert'],
      ];
      for (const [re, label] of checks) if (re.test(t)) return label;
      return null;
    }

    function priceFromLevel(level: number | null | undefined): string | null {
      if (level == null) return null; // 0..4
      return ['$', '$$', '$$$', '$$$$'][Math.min(Math.max(level, 0), 3)] || '$';
    }

    return {
      ...video,
      evidence_queries_used: queries,
      evidence_city_hint: nanoCityHint || locText || null,
      places_photo_url: bestPhoto || (video as any).places_photo_url || (video.thumbnail_url && video.thumbnail_url !== 'N/A' ? (video.thumbnail_url as any) : null),
      restaurant_name: d.name || video.restaurant_name || name || 'N/A',
      restaurant_address: d.formatted_address || video.restaurant_address || 'N/A',
      restaurant_location: video.restaurant_location || locText || 'N/A',
      restaurant_rating: typeof d.rating === 'number' ? d.rating : video.restaurant_rating,
      restaurant_cuisine: (video.restaurant_cuisine && video.restaurant_cuisine !== 'N/A') ? video.restaurant_cuisine : (mapCuisineFromTypes(d.types as any) || video.restaurant_cuisine),
      restaurant_price_range: (video.restaurant_price_range && video.restaurant_price_range !== 'N/A') ? video.restaurant_price_range : (priceFromLevel((d as any).price_level) || video.restaurant_price_range),
      google_maps_url: mapsUrl || video.google_maps_url || 'N/A',
      thumbnail_url: thumbnail,
      photo_attribution_html: photoAttribution ?? undefined,
      reviews,
  open_now: (d as any)?.opening_hours?.isOpen?.() ?? null,
  opening_hours_weekday_text: (d as any)?.opening_hours?.weekday_text || null,
  enrich_confidence: winner.score,
  enrich_place_name: (d as any)?.name || (winner.p as any)?.name || null,
      enrich_place_address: (d as any)?.formatted_address || null,
  enrich_place_id: (d as any)?.place_id || (winner.p as any)?.place_id || null,
      enrich_place_lat: typeof (d as any)?.geometry?.location?.lat === 'function' ? (d as any).geometry.location.lat() : (d as any)?.geometry?.location?.lat || null,
      enrich_place_lng: typeof (d as any)?.geometry?.location?.lng === 'function' ? (d as any).geometry.location.lng() : (d as any)?.geometry?.location?.lng || null,
      enrich_suggestions: suggestions,
    };
  } catch {
    // Fail soft — return original video object
    return video;
  }
}